import { forwardRef, useState } from "react";
import { Typography } from "@mui/material";
import {
  DISCARD_PRODUCING_CREATIVE_TEMPLATE_ALERT_DESCRIPTION,
  PRODUCING_CREATIVE_TEMPLATE_ERROR,
  PRODUCING_CREATIVE_TEMPLATE_MESSAGE,
  PRODUCING_CREATIVE_TEMPLATE_PARTIAL,
  PRODUCING_CREATIVE_TEMPLATE_PARTIAL_SINGULAR,
  PRODUCING_CREATIVE_TEMPLATE_PREVIEW_ERROR,
  PRODUCING_CREATIVE_TEMPLATE_PREVIEW_MESSAGE,
  PRODUCING_CREATIVE_TEMPLATE_PREVIEW_SUCCESS,
  PRODUCING_CREATIVE_TEMPLATE_SUCCESS,
} from "../../i18n/keysTranslations";
import { useTranslationApp } from "../../lib/i18next";
import {
  ALERTS_ASYNC_STATUS,
  CREATIVE_TEMPLATE_PRODUCE_INTENT,
} from "../../utils/constants";
import { CREATIVE_JOB_TYPES } from "../../utils/errors";
import LinearLoaderWithTime from "../Loaders/LinearLoaderWithTime";
import TaskAlertCard from "./TaskAlertCard";
import TaskAlertErrorMessage from "./TaskAlertErrorMessage";
import TaskAlertDiscardActions from "./TaskAlertDiscardActions";
import { useCreativeJobProgressLoader } from "../../hooks/useCreativeJobProgressLoader";
import { useTemplatesModals } from "../../contexts/templatesModalsContext";

const PRODUCE_ACTIVITY_TITLE_KEYS = {
  error: PRODUCING_CREATIVE_TEMPLATE_ERROR,
  success: PRODUCING_CREATIVE_TEMPLATE_SUCCESS,
  partial: PRODUCING_CREATIVE_TEMPLATE_PARTIAL,
  partialSingular: PRODUCING_CREATIVE_TEMPLATE_PARTIAL_SINGULAR,
  loading: PRODUCING_CREATIVE_TEMPLATE_MESSAGE,
};

const PREVIEW_ACTIVITY_TITLE_KEYS = {
  error: PRODUCING_CREATIVE_TEMPLATE_PREVIEW_ERROR,
  success: PRODUCING_CREATIVE_TEMPLATE_PREVIEW_SUCCESS,
  loading: PRODUCING_CREATIVE_TEMPLATE_PREVIEW_MESSAGE,
};

const getTaskVisualStatus = ({ isError, isPartial, isSuccess, isLoading }) => {
  if (isError) return "error";
  if (isPartial) return "warning";
  if (isSuccess) return "success";
  if (isLoading) return "inProgress";
  return "pending";
};

const ProducingCreativeTemplateAlertItem = forwardRef(
  ({ alert, onClose }, ref) => {
    const { t } = useTranslationApp();
    const { openTemplateEditor, openTemplateProduce } = useTemplatesModals();
    const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
    const { id, status, progress, generationData, message, messageKey, jobID } =
      alert;

    const summary =
      generationData?.summary || generationData?.jobResponse?.data?.summary;
    const failedCount = Number(summary?.failed) || 0;
    const succeededCount = Number(summary?.succeeded) || 0;
    const requestedCount =
      Number(summary?.requested) ||
      Number(generationData?.itemCount) ||
      succeededCount + failedCount;
    const isError = status === ALERTS_ASYNC_STATUS.ERROR;
    const isSuccess = status === ALERTS_ASYNC_STATUS.SUCCESS;
    const isPreviewIntent =
      generationData?.intent === CREATIVE_TEMPLATE_PRODUCE_INTENT.PREVIEW;
    const isPartial = isSuccess && failedCount > 0 && !isPreviewIntent;
    const isLoading =
      status === ALERTS_ASYNC_STATUS.LOADING ||
      status === ALERTS_ASYNC_STATUS.GENERATING;
    const isTerminal = isError || isSuccess;
    const storageKey = jobID ?? id;

    const {
      loaderTimeMs,
      onProgressChange,
      seedProgress,
      formattedRemainingTime,
    } = useCreativeJobProgressLoader({
      storageKey,
      migrateFromKey: id,
      jobType: CREATIVE_JOB_TYPES.produceCreativeTemplate,
      isTerminal,
      itemCount: generationData?.itemCount,
    });

    const titleKeys = isPreviewIntent
      ? PREVIEW_ACTIVITY_TITLE_KEYS
      : PRODUCE_ACTIVITY_TITLE_KEYS;

    const getTranslatedMessage = () => {
      if (isError) return t(titleKeys.error);
      if (isPartial) {
        return succeededCount === 1
          ? t(titleKeys.partialSingular, {
              requested: requestedCount,
            })
          : t(titleKeys.partial, {
              succeeded: succeededCount,
              requested: requestedCount,
            });
      }
      if (isSuccess) return t(titleKeys.success);
      if (isLoading) return t(titleKeys.loading);
      if (messageKey) return t(messageKey);
      return t(titleKeys.loading);
    };

    const onAlertClick = () => {
      if (isConfirmingDiscard) return;
      const templateID = generationData?.templateID;
      const produceID = generationData?.produceID;
      if (!templateID && !produceID) return;
      if (isPreviewIntent && templateID) {
        openTemplateEditor({
          templateID,
          initialPreviewProduceID: produceID,
        });
        return;
      }
      openTemplateProduce({
        templateID,
        produceID,
        language: generationData?.language,
        artboardIDs: generationData?.artboardIDs,
      });
    };

    const taskVisualStatus = getTaskVisualStatus({
      isError,
      isPartial,
      isSuccess,
      isLoading,
    });
    const displayProgress = progress || (isLoading ? seedProgress : 100);
    const showDiscardButton = isError || isSuccess;

    return (
      <TaskAlertCard
        ref={ref}
        status={taskVisualStatus}
        label={getTranslatedMessage()}
        estimatedTime={isLoading ? formattedRemainingTime : null}
        progress={displayProgress}
        onClick={onAlertClick}
        cursor="pointer"
        showProgressBar={!isLoading}
        showProgressPercent={!isLoading}
        progressSlot={
          isLoading ? (
            <LinearLoaderWithTime
              isAlert={true}
              labelSide="bottom"
              time={loaderTimeMs}
              progress={seedProgress}
              autoIncrement={true}
              max={99}
              onProgressChange={onProgressChange}
            />
          ) : null
        }
        details={
          message ? (
            isError ? (
              <TaskAlertErrorMessage>
                <Typography
                  variant="body2"
                  color="text.primary"
                  sx={{ fontSize: "0.75rem" }}
                >
                  {message}
                </Typography>
              </TaskAlertErrorMessage>
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {message}
              </Typography>
            )
          ) : null
        }
        actions={
          showDiscardButton ? (
            <TaskAlertDiscardActions
              isConfirming={isConfirmingDiscard}
              descriptionKey={
                DISCARD_PRODUCING_CREATIVE_TEMPLATE_ALERT_DESCRIPTION
              }
              onStartDiscard={() => {
                if (isSuccess) {
                  setIsConfirmingDiscard(true);
                  return;
                }
                onClose(id);
              }}
              onConfirm={() => onClose(id)}
              onCancel={() => setIsConfirmingDiscard(false)}
              onView={isSuccess ? onAlertClick : null}
            />
          ) : null
        }
      />
    );
  }
);

ProducingCreativeTemplateAlertItem.displayName =
  "ProducingCreativeTemplateAlertItem";

export default ProducingCreativeTemplateAlertItem;
